export type Platform = 'ios' | 'android' | 'desktop';
export type Browser = 'safari' | 'chrome' | 'samsung' | 'other';

export interface DeviceInfo {
  platform: Platform;
  browser: Browser;
  browserVersion: number | null;
  osVersion: number | null;
  isInApp: boolean;
}

const getPlatform = (ua: string): Platform => {
  const isIos = /iPhone|iPod/i.test(ua) || (/Mac/i.test(ua) && 'ontouchend' in document);
  const isAndroid = /Android/i.test(ua);

  if (isIos) return 'ios';
  if (isAndroid) return 'android';

  return 'desktop';
};

const getOsVersion = (ua: string, platform: Platform): number | null => {
  if (platform === 'ios') {
    const match = ua.match(/OS (\d+)_(\d+)/);
    return match ? parseFloat(`${match[1]}.${match[2]}`) : null;
  }
  if (platform === 'android') {
    const match = ua.match(/Android (\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : null;
  }
  return null;
};

const isTelegram = (): boolean =>
  typeof (window as Window & { TelegramWebviewProxy?: unknown }).TelegramWebviewProxy !== 'undefined';

const getIsInApp = (ua: string): boolean =>
  isTelegram() ||
  /FBAN|FBAV|Instagram|VKWebView|VKAndroid|OKApp|WhatsApp|TwitteriPhone|TwitterAndroid|musical_ly|BytedanceWebview|Snapchat|MicroMessenger|Line\//i.test(
    ua,
  );

const getBrowser = (ua: string): Pick<DeviceInfo, 'browser' | 'browserVersion'> => {
  // Chrome on iOS
  const criosMatch = ua.match(/CriOS\/(\d+\.?\d*)/);
  if (criosMatch) {
    return { browser: 'chrome', browserVersion: parseFloat(criosMatch[1]) };
  }

  // Samsung Internet (before Chrome check — Samsung UA also contains Chrome/)
  const samsungMatch = ua.match(/SamsungBrowser\/(\d+\.?\d*)/);
  if (samsungMatch) {
    return { browser: 'samsung', browserVersion: parseFloat(samsungMatch[1]) };
  }

  // Chrome on Android / Desktop (exclude Edge)
  const chromeMatch = ua.match(/Chrome\/(\d+\.?\d*)/);
  if (chromeMatch && !/Edg\//i.test(ua)) {
    return { browser: 'chrome', browserVersion: parseFloat(chromeMatch[1]) };
  }

  // Safari (exclude other iOS browsers)
  const safariMatch = ua.match(/Version\/(\d+\.?\d*)/);
  if (safariMatch && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)) {
    return { browser: 'safari', browserVersion: parseFloat(safariMatch[1]) };
  }

  return { browser: 'other', browserVersion: null };
};

export const getDeviceInfo = (ua = navigator.userAgent ?? ''): DeviceInfo => {
  const platform = getPlatform(ua);
  const osVersion = getOsVersion(ua, platform);
  const { browser, browserVersion } = getBrowser(ua);
  const isInApp = getIsInApp(ua);

  return { platform, browser, browserVersion, osVersion, isInApp };
};
